'use client';

import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import dynamic from 'next/dynamic';
import Image from 'next/image';

import uploadIcon from '@/assets/icons/pet-profile-imgae-uploader-icon.svg';
import uploadPlaceholderIcon from '@/assets/icons/pet-profile-uploading-profile-placeholder-icon.svg';
import catProfileImage from '@/assets/images/cat-placeholder.png';
import dogProfileImage from '@/assets/images/dog-placeholder.png';
import uploadingMessageImage from '@/assets/images/uploading-loading-icon.svg';
import { useAuthContext } from '@/components/Providers/AuthProvider';
import cn from '@/helpers/utils/classNames';
import { isPuptimusPrime } from '@/helpers/utils/customer';
import { useAppContext } from '@/layouts/AppProvider';
import { useUpdatePetProfileMutation } from '@/store/api/petProfile';
import { PetImageObj } from '@/store/slices/petProfile';

const Button = dynamic(() => import('../../Button'));
const ImageCropper = dynamic(() => import('../ImageCropper'));
const Modal = dynamic(() => import('../Modals'));
const Spinner = dynamic(() => import('../Spinner'));

interface UploadProfileProps {
  imageUrl: string;
  profileId: number;
  isDog: boolean;
  onProfilePictureChange: (updatedPetImage: PetImageObj | undefined, profileId: number) => void;
  croppedImageUrl: string;
  proportion: string;
}

const parseProportions = (proportions: string) => {
  try {
    return JSON.parse(proportions);
  } catch (err) {
    console.error({ err, proportions }, 'error while parsing proportions');

    return {
      rotation: 0,
      x: 0,
      y: 0,
      zoom: 1,
    };
  }
};

const UploadProfile = ({
  proportion,
  croppedImageUrl,
  imageUrl,
  profileId,
  isDog,
  onProfilePictureChange,
}: UploadProfileProps) => {
  const inputFileRef = useRef<HTMLInputElement | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDroppingFile, setIsDroppingFile] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(true);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'uploaded' | 'canceled'>('idle');

  const [file, setFile] = useState<File | null>(null);
  const [updatePetProfileImage, { isLoading }] = useUpdatePetProfileMutation();

  const [previewUrl, setPreviewUrl] = useState<string>('');

  const uploadImageCallRef = useRef<ReturnType<typeof updatePetProfileImage>>();

  const uploadCanceledTimeoutRef = useRef<NodeJS.Timeout>();

  const { customer } = useAuthContext();
  const { isBackOffice } = useAppContext();

  const [editablePetImage, setEditablePetImage] = useState<PetImageObj>({
    orignalImage: imageUrl,
    croppedImage: croppedImageUrl,
    proportion: proportion
      ? parseProportions(proportion)
      : {
          rotation: 0,
          x: 0,
          y: 0,
          zoom: 1,
        },
  });

  const [isSaveEnabled, setIsSaveEnabled] = useState(false);

  const onDroping = (evt: any) => {
    if (uploadCanceledTimeoutRef.current) clearTimeout(uploadCanceledTimeoutRef.current);
    evt.preventDefault();
    evt.stopPropagation();

    if (isLoadingImage) return;

    const droppedFiles = evt.dataTransfer.files;
    if (droppedFiles.length > 0) {
      const newFile = droppedFiles[0];

      if (!newFile || !newFile.type || !['image/png', 'image/jpeg', 'image/jpg'].includes(newFile.type.toLowerCase())) {
        toast.error('File not supported. Please upload the picture in .png, .jpeg or .jpg format.');
        setIsLoadingImage(false);
        setIsDroppingFile(false);
        return;
      }

      const maxSizeInBytes = 5 * 1024 * 1024;
      if (newFile.size > maxSizeInBytes) {
        toast.error('Please upload an image with a maximum size of 5 MB.');
        setIsLoadingImage(false);
        return;
      }

      setFile(newFile);
    }
    setIsDroppingFile(false);
  };

  const handleChange = (evt: any) => {
    if (uploadCanceledTimeoutRef.current) clearTimeout(uploadCanceledTimeoutRef.current);
    if (isLoadingImage) return;
    setIsLoadingImage(true);
    if (!evt.target.files || evt.target.files.length === 0) {
      setFile(null);
      return;
    }

    const selectedFile = evt.target.files[0];

    if (
      !selectedFile ||
      !selectedFile.type ||
      !['image/png', 'image/jpeg', 'image/jpg'].includes(selectedFile.type.toLowerCase())
    ) {
      toast.error('File not supported. Please upload the picture in .png, .jpeg or .jpg format.');
      setIsLoadingImage(false);
      return;
    }

    const maxSizeInBytes = 5 * 1024 * 1024;
    if (selectedFile.size > maxSizeInBytes) {
      toast.error('Please upload an image with a maximum size of 5 MB.');
      setIsLoadingImage(false);
      return;
    }

    setFile(selectedFile);
  };

  useEffect(() => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setPreviewUrl(base64String);
        setIsLoadingImage(false);
        setIsSaveEnabled(true);
      };
      reader.readAsDataURL(file);
    }
  }, [file]);

  const uploadImage = async () => {
    if (uploadCanceledTimeoutRef.current) clearTimeout(uploadCanceledTimeoutRef.current);
    setIsSaveEnabled(false);

    const convertUrlToBase64 = async (url: string): Promise<string> => {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    };

    const isBase64 = (str: string) => str.startsWith('data:image');

    let finalOriginalImage = editablePetImage.orignalImage;
    let finalCroppedImage = editablePetImage.croppedImage;

    if (finalOriginalImage && !isBase64(finalOriginalImage)) {
      finalOriginalImage = await convertUrlToBase64(finalOriginalImage);
    }

    if (finalCroppedImage && !isBase64(finalCroppedImage)) {
      finalCroppedImage = await convertUrlToBase64(finalCroppedImage);
    }

    const updatedEditablePetImage = {
      ...editablePetImage,
      orignalImage: finalOriginalImage || '',
      croppedImage: finalCroppedImage || '',
    };

    const payload = {
      profileId,
      body: updatedEditablePetImage,
    };

    try {
      setUploadStatus('uploading');
      setPreviewUrl('');
      setFile(null);
      uploadImageCallRef.current = updatePetProfileImage(payload);
      const response = await uploadImageCallRef.current.unwrap();
      console.log(response, 'response of image upload');

      setUploadStatus('uploaded');
      setPreviewUrl(response?.data?.orignalImage || '');
      if (response.data) setEditablePetImage(response.data);
      onProfilePictureChange(response.data, profileId);
      toast.success('The image has been uploaded.');
      setTimeout(() => {
        setIsSaveEnabled(false);
        setIsUploadModalOpen(false);
        setUploadStatus('idle');
      }, 2000);
    } catch (error: any) {
      console.log(error, 'image upload error');

      if (error?.name === 'AbortError') {
        setUploadStatus('canceled');
        uploadCanceledTimeoutRef.current = setTimeout(() => {
          setUploadStatus('idle');
        }, 1000);
        return;
      }

      setUploadStatus('idle');
      const errorMessage = error?.data?.message || 'An error occurred. Please try again later.';
      toast.error(errorMessage);
    }
  };

  const handleDragOver = (evt: any) => {
    evt.preventDefault();
    evt.stopPropagation();
    if (isLoadingImage) return;
    setIsDroppingFile(true);
  };

  const handleDragEnd = () => {
    if (isLoadingImage) return;
    setIsDroppingFile(false);
  };

  useEffect(() => {
    setTimeout(() => {
      setIsLoadingImage(false);
    }, 500);
  }, [previewUrl]);

  const disabledUploadPhoto =
    (isBackOffice && customer && !isPuptimusPrime(customer)) || (isBackOffice && customer === undefined);

  return (
    <div>
      <div
        className="v2-relative group/item v2-w-[75px] v2-h-[75px]"
        onClick={() => {
          setPreviewUrl(imageUrl);
          setIsUploadModalOpen(true);
        }}
        style={disabledUploadPhoto ? { pointerEvents: 'none' } : {}}
      >
        <Image
          unoptimized
          src={`${croppedImageUrl || (isDog ? dogProfileImage.src : catProfileImage.src)}?t=${new Date().getTime()}`}
          width={75}
          height={75}
          alt="pet quiz"
          className="v2-rounded-full v2-object-cover v2-h-[75px] v2-w-[75px]"
        />
        <div className="v2-w-full v2-h-full v2-bg-white v2-bg-opacity-60 v2-absolute v2-top-0 v2-cursor-pointer v2-rounded-full v2-justify-center v2-items-center v2-flex invisible group-hover/item:visible">
          <Image src={uploadPlaceholderIcon} alt="update profile image" />
        </div>
      </div>

      <Modal
        title="Upload Photo"
        className="md:!v2-w-[400px] sm:!v2-w-[311px] xs:!v2-w-[311px]"
        isOpen={isUploadModalOpen}
        onClose={() => {
          setIsUploadModalOpen(false);
          if (!previewUrl) {
            setEditablePetImage(pi => ({
              ...pi,
              proportion: pi.oldProportion || pi.proportion,
            }));
          }
        }}
      >
        <p className="v2-font-sweet-sans-pro-light v2-text-base v2-text-[#101828]">
          Add a photo. Show us your furr baby!
        </p>

        <div
          className={cn(
            'v2-relative v2-border-dotted v2-border v2-flex v2-justify-center focus-within:v2-bg-gray-100 v2-items-center v2-cursor-pointer v2-border-black v2-rounded-xs v2-mt-3 v2-w-[263px] v2-h-[320px] md:v2-w-[352px] md:v2-h-[320px]',
            { 'v2-bg-[#C5D1CC] !v2-border-solid !v2-border-[6px] !v2-border-[#154734]': isDroppingFile },
            {
              'v2-bg-[#C5D1CC] !v2-border-solid !v2-border-[1px] !v2-border-[#EAECF0] !v2-cursor-default':
                isLoadingImage || isLoading,
            },
            { 'v2-hidden': previewUrl }
          )}
          role="button"
          onDragOver={handleDragOver}
          onDragLeave={handleDragEnd}
          onDrop={onDroping}
        >
          <div className="v2-flex v2-flex-col v2-items-center v2-gap-[21px] v2-text-cente">
            {!isDroppingFile ? (
              <>
                <Image src={uploadIcon} alt="uploading image icon" className="" />
                <p className="v2-font-sweet-sans-pro v2-text-center v2-leading-[18px] v2-text-sm v2-text-[#333333]">
                  Drag & drop file here <br />
                  or
                  <br /> browse your gallery.
                </p>
                <Button
                  onClick={() => {
                    if (isLoadingImage) return;
                    inputFileRef?.current?.click();
                  }}
                  className="!v2-border-[#154734] !v2-text-[#123E2E] !v2-text-[14px]"
                  type="button"
                  as="button"
                  btnSize="md"
                  scheme="secondary"
                  variant="outlined"
                >
                  Browse Files
                </Button>
              </>
            ) : (
              <p className="v2-font-sweet-sans-pro v2-text-2xl v2-text-[#333333]">Drop File Here</p>
            )}
          </div>
          {isLoadingImage ||
            (isLoading && (
              <div className="v2-absolute v2-top-0 v2-left-0 v2-w-full v2-pointer-events-none v2-bg-white v2-h-full v2-flex v2-justify-center v2-items-center ">
                <div className="">
                  <Spinner className="v2-flex v2-justify-center" />
                  <p className="v2-font-sweet-sans-pro v2-text-sm v2-text[#344054] v2-mt-4">Loading...</p>
                </div>
              </div>
            ))}
        </div>
        {previewUrl && (
          <div
            className={cn('v2-relative v2-border v2-rounded-xs v2-mt-3', {
              'v2-blcok': previewUrl,
            })}
          >
            <ImageCropper
              key={`cropper-${JSON.stringify(editablePetImage)}`}
              src={previewUrl || editablePetImage.orignalImage}
              PetImage={editablePetImage}
              onRemoveImageClick={() => {
                setEditablePetImage(pi => ({
                  orignalImage: '',
                  croppedImage: '',
                  oldProportion: pi.proportion,
                  proportion: { zoom: '0', x: '0', y: '0', rotation: '0' },
                }));
                setIsSaveEnabled(false);
                setPreviewUrl('');
                setFile(null);
              }}
              imageChangeHandler={value => {
                console.log('CHANGE => ', value);
                setEditablePetImage(value);
                setIsSaveEnabled(true);
              }}
            />
          </div>
        )}

        <input key={`input-file-${file}`} type="file" ref={inputFileRef} hidden onChange={handleChange} />

        <div className="v2-pt-8">
          <Button
            type="button"
            as="button"
            btnSize="md"
            scheme="primary"
            variant="filled"
            className={cn(
              !(!isSaveEnabled || isLoading || (!imageUrl && !previewUrl)) && '!v2-bg-[#154734]',
              'v2-w-full'
            )}
            onClick={() => uploadImage()}
            disabled={isLoading || !previewUrl}
          >
            Save
          </Button>
        </div>
        {uploadStatus === 'uploading' && (
          <div className="v2-w-[350px] md:v2-w-[350px] sm:v2-w-[263px] xs:v2-w-[263px] v2-rounded-xs v2-p-[16px] v2-bg-white v2-fixed v2-bottom-[12%] v2-shadow-lg">
            <div className="v2-flex v2-justify-between v2-items-center">
              <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-sm v2-leading-[19px] v2-text-[#101828] v2-flex v2-gap-[8px] v2-items-center">
                <Image src={uploadingMessageImage} alt="Uploading" className="v2-w-[22px]" />
                Uploading
              </p>
              <p
                className="v2-font-sweet-sans-pro v2-font-normal v2-text-sm v2-leading-[19px] v2-text-[#101828]"
                onClick={() => uploadImageCallRef.current?.abort()}
              >
                Cancel
              </p>
            </div>
          </div>
        )}

        {uploadStatus === 'uploaded' && (
          <div className="v2-w-[350px] md:v2-w-[350px] sm:v2-w-[263px] xs:v2-w-[263px] v2-rounded-xs v2-p-[16px] v2-bg-white v2-fixed v2-bottom-[12%] v2-shadow-lg">
            <div className="v2-w-full">
              <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-sm v2-leading-[19px] v2-text-[#101828] v2-text-center v2-flex v2-justify-center v2-self-center">
                Uploaded!
              </p>
            </div>
          </div>
        )}

        {uploadStatus === 'canceled' && (
          <div className="v2-w-[350px] md:v2-w-[350px] sm:v2-w-[263px] xs:v2-w-[263px] v2-rounded-xs v2-p-[16px] v2-bg-white v2-fixed v2-bottom-[12%] v2-shadow-lg">
            <div className="v2-w-full">
              <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-sm v2-leading-[19px] v2-text-[#101828] v2-text-center v2-flex v2-justify-center v2-self-center">
                Canceled!
              </p>
            </div>
          </div>
        )}

        {isDroppingFile && (
          <div className="v2-w-[350px] md:v2-w-[350px] sm:v2-w-[263px] xs:v2-w-[263px] v2-rounded-xs v2-p-[16px] v2-bg-white v2-fixed v2-bottom-[12%] v2-shadow-lg">
            <div className="v2-w-full">
              <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-sm v2-leading-[19px] v2-text-[#101828] v2-text-center v2-flex v2-justify-center v2-self-center">
                Drop the photo to upload it
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UploadProfile;
